import * as React from "react";
import { WarningCircleIcon, type Icon } from "@phosphor-icons/react";
import { cn } from "cn";

import { Button } from "@libs/zapi-ui/ds/components/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from "@libs/zapi-ui/ds/components/card";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@libs/zapi-ui/ds/components/empty";
import { Label } from "@libs/zapi-ui/ds/components/label";
import { Skeleton } from "@libs/zapi-ui/ds/components/skeleton";
import { TextReveal } from "@libs/zapi-ui/ds/components/text-reveal";

type SettingsNavItem<TValue extends string> = { value: TValue; label: string; icon: Icon };

type SettingsNavGroup<TValue extends string> = { id: string; label: string; items: SettingsNavItem<TValue>[] };

function SettingsHeader({
  title,
  description,
  actions,
  className
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header
      data-slot="settings-header"
      className={cn("flex flex-wrap items-end justify-between gap-x-6 gap-y-3", className)}
    >
      <div className="flex min-w-0 max-w-xl flex-col gap-1.5">
        <TextReveal as="h1" className="typo-h3 text-foreground">
          {title}
        </TextReveal>
        {description && <p className="typo-body-md text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

function SettingsSection({
  title,
  description,
  actions,
  footer,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"div">, "title"> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Controls beside the title: the section's Save (a SettingsSectionActions), a search. */
  actions?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const titleId = React.useId();
  return (
    <Card
      role="region"
      aria-labelledby={title ? titleId : undefined}
      data-slot="settings-section"
      className={cn("gap-0 py-0", className)}
      {...props}
    >
      {(title || description || actions) && (
        <CardHeader
          className={cn(
            "flex flex-wrap items-center justify-between gap-x-4 gap-y-3 px-5 pt-5 sm:px-6",
            !children && "pb-5"
          )}
        >
          <div className="flex min-w-0 flex-1 basis-60 flex-col gap-1">
            {title && (
              <h2 id={titleId} className="typo-h6 text-foreground">
                {title}
              </h2>
            )}
            {description && <CardDescription className="typo-body-sm">{description}</CardDescription>}
          </div>
          {actions && <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">{actions}</div>}
        </CardHeader>
      )}
      {children && <CardContent className="gap-5 px-5 py-5 sm:px-6">{children}</CardContent>}
      {footer && <CardFooter className="border-t bg-muted/40 px-5 py-3 sm:px-6">{footer}</CardFooter>}
    </Card>
  );
}

function SettingsSectionActions({
  status,
  className,
  children
}: {
  /** What the buttons act on, such as "Unsaved changes". */
  status?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-slot="settings-section-actions"
      className={cn("flex w-full flex-wrap items-center gap-x-4 gap-y-2", className)}
    >
      {status && (
        <span role="status" className="typo-caption flex items-center gap-1.5 text-muted-foreground">
          {status}
        </span>
      )}
      <div className="ms-auto flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function SettingsRow({
  label,
  description,
  htmlFor,
  layout = "inline",
  className,
  children
}: {
  label: React.ReactNode;
  description?: React.ReactNode;
  /** The control's id, so the label focuses it. */
  htmlFor?: string;
  /** inline puts the control at the row's end (switches, short selects); stacked puts it under the label (inputs, text areas). */
  layout?: "inline" | "stacked";
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-slot="settings-row"
      data-layout={layout}
      className={cn(
        "flex flex-col gap-2.5 data-[layout=inline]:sm:flex-row data-[layout=inline]:sm:items-center data-[layout=inline]:sm:justify-between data-[layout=inline]:sm:gap-6",
        className
      )}
    >
      <div className="flex min-w-0 flex-col gap-1 sm:max-w-md">
        {htmlFor ? (
          <Label htmlFor={htmlFor} className="typo-label-md text-foreground">
            {label}
          </Label>
        ) : (
          <span className="typo-label-md text-foreground">{label}</span>
        )}
        {description && <p className="typo-body-sm text-muted-foreground">{description}</p>}
      </div>
      <div className={cn("flex min-w-0 items-center gap-2", layout === "stacked" ? "w-full" : "shrink-0")}>
        {children}
      </div>
    </div>
  );
}

function SettingsValue({
  icon: ValueIcon,
  className,
  children
}: {
  icon?: Icon;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      data-slot="settings-value"
      className={cn("typo-body-sm flex min-w-0 items-center gap-2 text-foreground", className)}
    >
      {ValueIcon && <ValueIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
      <span className="truncate">{children}</span>
    </span>
  );
}

function SettingsRowGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="settings-row-group"
      className={cn("flex flex-col divide-y *:py-4 *:first:pt-0 *:last:pb-0", className)}
      {...props}
    />
  );
}

function SettingsSectionSkeleton({ rows = 2, media = false }: { rows?: number; media?: boolean }) {
  return (
    <Card data-slot="settings-section-skeleton" aria-busy="true" className="gap-0 py-0">
      <div className="flex flex-col gap-2 px-5 pt-5 sm:px-6">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-4 w-64 max-w-full" />
      </div>
      <div className="flex flex-col gap-5 px-5 py-5 sm:px-6">
        {media && (
          <div className="flex items-center gap-5">
            <Skeleton className="size-20 rounded-full" />
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-32 rounded-full" />
              <Skeleton className="h-3 w-48" />
            </div>
          </div>
        )}
        {Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-9 w-full max-w-sm" />
          </div>
        ))}
      </div>
    </Card>
  );
}

function SettingsError({
  title,
  description,
  retryLabel = "Try again",
  onRetry,
  icon: ErrorIcon = WarningCircleIcon,
  className
}: {
  title: string;
  description?: React.ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
  icon?: Icon;
  className?: string;
}) {
  return (
    <Empty data-slot="settings-error" role="alert" className={cn("rounded-xl border bg-card p-8 sm:p-10", className)}>
      <EmptyHeader>
        <EmptyMedia variant="icon" className="bg-destructive/10 text-destructive">
          <ErrorIcon />
        </EmptyMedia>
        <EmptyTitle className="typo-h6">{title}</EmptyTitle>
        {description && <EmptyDescription>{description}</EmptyDescription>}
      </EmptyHeader>
      {onRetry && (
        <EmptyContent>
          <Button variant="outline" shape="pill" onClick={onRetry}>
            {retryLabel}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}

export {
  SettingsError,
  SettingsHeader,
  SettingsRow,
  SettingsRowGroup,
  SettingsSection,
  SettingsSectionActions,
  SettingsSectionSkeleton,
  SettingsValue,
  type SettingsNavGroup,
  type SettingsNavItem
};
